import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { UserService } from '../user/UserService';
import { AppState } from "../RootReducer";
import { getContainer } from "../ioc/IOCSetup";
import { Spinner } from "../ui/Spinner";
import { loginResponse } from '../user/UserActions';
import { LoginVerification } from "../http/protocol";
import { deviceDetect } from 'react-device-detect';
import { ManualLoginRequest, LoginResponseStatus, MultiFactorAuthProvider } from '../http/protocol';
import { mfaRequired, mfaFailed, manualApprovalRequired, termsAcceptRequired, fetchUserInfo, showPushPrompt, requestVerifyEmail} from '../user/UserActions';
import { Modal, Button } from 'react-bootstrap';
import LandingPage from '../landingpage/LandingPage';
import '../landingpage/styles/LandingPage.scss';
import { t } from "../i18n";

const LOCAL_STORAGE_KEYS = {
    USER_ID: "userId",
    TOKEN: "token",
    SALT: "salt",
    TOKEN_REF_ID: "tokenRefId"
};

const VerifyEmailByLinkAccount: React.FC = () => {
    const settings = useSelector((state: AppState) => state.settings.settings);
    const appMode = useSelector((state: AppState) => state.user.appMode);
    const device = deviceDetect(window.navigator.userAgent ? window.navigator.userAgent : "");
    let fpID: string = "";
    const location = useLocation();
    const history = useHistory();
    const userService: UserService = getContainer().getUserService();
    const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const dispatch = useDispatch();

    const handleClose = () => {
        setShowVerifyEmailModal(false);
        history.push('/');
    };

    const setLocalStorageItems = (response: any) => {
        localStorage.setItem(LOCAL_STORAGE_KEYS.USER_ID, response.userUid);
        localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, response.token);
        localStorage.setItem(LOCAL_STORAGE_KEYS.SALT, response.secret);
        localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN_REF_ID, response.tokenRefId.toString());
    };

    const handlePushNotifications = async (response: any) => {
        let loginsRequired = settings?.loginsRequiredForPushPrompt ?? null;
        let pushEnabled: boolean = !!settings?.pushNotificationsEnabled;
        let noOfLogins = response.userSettings?.["noOfLogin"];
        let optOutPushPrompt = response.userSettings?.["optOutPushPrompt"];

        if (pushEnabled && noOfLogins != null && loginsRequired != null && (optOutPushPrompt == null)) {
            if ((Number(noOfLogins) + 1) >= Number(loginsRequired)) {
                try {
                    if (navigator?.serviceWorker?.controller) {
                        const swRegistration = await navigator.serviceWorker.ready;
                        const subscription = await swRegistration.pushManager.getSubscription();
                        if (!subscription) {
                            const permissionState = await swRegistration.pushManager.permissionState({ userVisibleOnly: true });
                            if (permissionState === "prompt") {
                                dispatch(showPushPrompt(true));
                            }
                        }
                    }
                } catch (e) {
                    console.log("Failed to check if user is subscribed: ", e);
                }
            }
        }
    };

    const logError = (message: string, error: any) => {
        console.error(message, error);
        setModalMessage(message);
        setShowVerifyEmailModal(true);
    };

    useEffect(() => {
        const fetchFingerprint = async () => {
            try {
                fpID = (await window.fingerprint.get()).visitorId;
            } catch (e) {
                console.log("Failed to get fingerprint id");
            }
        };

        fetchFingerprint();

        const queryParams = new URLSearchParams(location.search);
        const userUid = queryParams.get('userUid');
        const code = queryParams.get('code');
        let updatedReq: ManualLoginRequest = {
            appMode: appMode,
            device: device.os || device.osName || undefined,
            deviceId: fpID,
            verification: LoginVerification.EMAIL
        };
        const isFingerprintPro = window.fingerprintPro !== undefined;

        if (userUid && code) {
            userService.verifyLinkCode(userUid, code, true)
            .then(async (response) => {
                if (response.emailVerified === false) {
                    throw new Error('Verification failed');
                }
                console.log('Verification response:', response);
                if (response.userUid) {
                    localStorage.setItem("userId", response.userUid);
                    if (isFingerprintPro) {
                        try {
                            window.fingerprintPro.get({ tag: { uid: response.userUid } });
                        } catch (e) {
                            console.log("Failed to get fingerprintpro!");
                        }
                    }
                }

                if ((response.responseStatus === LoginResponseStatus.MULTI_FACTOR_AUTH_REQUIRED)) {
                    if (Object.keys(MultiFactorAuthProvider).some(k => k == response.errorMessage)) {
                        dispatch(mfaRequired(response.errorMessage as MultiFactorAuthProvider, response.userUid, code));
                    } else {
                        dispatch(mfaFailed());
                    }
                } else if (response.responseStatus === LoginResponseStatus.MULTI_FACTOR_AUTH_FAILED) {
                    dispatch(mfaFailed());
                } else if (response.responseStatus === LoginResponseStatus.ACCEPT_TERMS_REQUIRED) {
                    const versionToAccept = response.responseContext ?? "";
                    dispatch(termsAcceptRequired(versionToAccept, updatedReq));
                } else if (response.responseStatus === LoginResponseStatus.MANUAL_APPROVAL_REQUIRED) {
                    dispatch(manualApprovalRequired());
                } else if (response.loggedIn === false) {
                    throw new Error(response.errorMessage);
                } else {
                    await handlePushNotifications(response);
                    if (response.token && response.secret && response.tokenRefId) {
                        setLocalStorageItems(response);
                    }

                    if (response.responseStatus === LoginResponseStatus.VERIFICATION_REQUIRED) {
                        let newDevice = false;
                        if (response.emailVerified === true && response.verificationChannel == LoginVerification.EMAIL) {
                            newDevice = true;
                        } else if (response.phoneNumberVerified === true && response.verificationChannel == LoginVerification.PHONE) {
                            newDevice = true;
                        }
                        dispatch(requestVerifyEmail(updatedReq, newDevice) as any);
                    } else {
                        response.loggedIn = true;
                        dispatch(loginResponse(response));
                        console.log("fetching user info!");
                        dispatch(fetchUserInfo(true) as any);
                        history.push('/casino');
                    }
                }
            })
            .catch((error) => {
                logError('Verification failed', error);
                userService.fetchUserInfo().then((user) => {
                    //if verification failed and user is not logged in, show link expired message
                    if(!user?.userUid){
                        setModalMessage(t("verifyEmailByLink.link-expired"));
                        setShowVerifyEmailModal(true);
                    }
                    //if verification failed and user is logged in, show already verified message
                    else if(user?.userUid === userUid) {
                        setModalMessage(t("verifyEmailByLink.already-verified"));
                        setShowVerifyEmailModal(true);
                    }
                    //if verification failed and user is logged in on other account, show already logged in on other account message
                    else if(user?.userUid && user?.userUid !== userUid) {
                        setModalMessage(t("verifyEmailByLink.logged-in-other-account"));
                        setShowVerifyEmailModal(true);
                    }
                });
            });
        }
    }, [location.search, userService, history, dispatch]);

    return (
        <div>
            <LandingPage />
            <Modal show={showVerifyEmailModal} onHide={handleClose} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{t("verifyEmailByLink.verification-failed-title")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{modalMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleClose}>
                        {t("verifyEmailByLink.close")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default VerifyEmailByLinkAccount;